import intercept from '@xfe-team/utils/src/intercept';
import UnmountError from '@src/error/UnmountError';
import invariant from 'invariant';
import EndThreadError from '@src/error/EndThreadError';
import { delay } from '@xfe-team/utils/src/common-util';
import reload from '@xfe-team/utils/src/reload';
import DeviceUtils from '@xfe-team/utils/src/device-utils';
import { observable } from 'mobx';
import protect from '@src/error/protect';

export default class BaseViewModel {
  /**
   * 回收队列
   * @example
   *
   * componentDidMount() {
   *   window.addEventListener('click', this.onWindowClick, false);
   *   this.$$recycleQueue.push(() => window.removeEventListener('click', this.onWindowClick, false));
   * }
   *
   * @type {Array}
   */
  $$recycleQueue = [];

  $$componentDidMountQueue = [];

  /**
   * 服务数据状态
   * @type {{}}
   */
  @observable serviceState = {};

  constructor(context) {
    BaseViewModel.checkIfReactClassContext(context);
    this.context = context;
    this.createInterceptions();
    this.createEventListeners();
    this.createTime = new Date().getTime();
    // noinspection JSUnresolvedVariable
    if (this.context.props.$prefetchProps) {
      // noinspection JSUnresolvedVariable
      this.serviceState = this.context.props.$prefetchProps;
    }
  }

  /**
   * @private
   */
  createEventListeners() {
    const visibilityChangeListener = () => {
      // 监听网页是否进行唤起活动页面的操作 释义：用户进行息屏和灭屏或者切换到了其他应用后重新回到了当前页面
      document.hidden ? this.$$onHide() : this.$$onShow();
    };
    // noinspection SpellCheckingInspection
    document.addEventListener('visibilitychange', visibilityChangeListener);
    // noinspection SpellCheckingInspection
    this.enqueueRecycle(() => document.removeEventListener('visibilitychange', visibilityChangeListener));

    /**
     * 在手机连接断网和恢复联网时给用户进行相关提示
     * @param type
     */
    const onConnectionChange = ({ type }) => {
      if (type === 'offline') {
        this.$$onOffLine();
      } else if (type === 'online') {
        this.$$onOnline();
      } else {
        throw new Error('unknown network connection type = ' + type);
      }
    };

    window.addEventListener('online', onConnectionChange);
    window.addEventListener('offline', onConnectionChange);
  }

  /**
   * @private
   */
  static checkIfReactClassContext(context) {
    invariant(context && context.isReactComponent, '请正确传入 react class 实例对象 this');
  }

  /**
   * 入队回收队列
   * @public
   * @param {Function} disposalAction - 回收操作
   */
  enqueueRecycle(disposalAction) {
    this.$$recycleQueue.push(disposalAction);
  }

  /**
   * 销毁时触发 action
   * enqueueRecycle alias
   * @type {BaseViewModel.enqueueComponentWillUnmount}
   */
  enqueueComponentWillUnmount = this.enqueueRecycle;

  enqueueComponentDidMount(action) {
    this.$$componentDidMountQueue.push(action);
  }

  /**
   * wp => 缩写 wrapPromise, 用于包裹 promise, 用于解决 promise 在组件卸载后的副作用
   * @param {Promise|Function} promise - promise 对象或 function
   * @return {*}
   */
  wp(promise) {
    if (typeof promise === 'function') {
      promise = promise();
    }
    return promise.then(
      result => {
        this.mayThrowUnmountError();
        return result;
      },
      err => {
        this.mayThrowUnmountError();
        throw err;
      }
    );
  }

  /**
   * 可能会抛出"组件已卸载"异常
   */
  mayThrowUnmountError() {
    if (this.$$isUnmount) {
      throw new UnmountError();
    }
  }

  delay(ms) {
    return this.wp(delay(ms));
  }

  /**
   * 微任务回调
   * @param {Function} action - 操作事件
   * @return {Promise<void>}
   */
  nextTick(action) {
    return Promise.resolve().then(action);
  }

  /**
   * 重定向辅助方法,
   * 基本用法:
   * this.redirect('/detail/1', {query: {name: 'allen'}})
   * => /detail/1?name=allen
   *
   * @param {string} routePath - 重定向地址
   * @param {string} [type] - 重定向类型, 支持 push, replace, 默认: push
   * @param {object} [params] - 路由传入参数, 如: {name: 123, gender: 456}
   * @param {boolean} [endThread] - 是否中断线程
   * @param {object} query - query string
   */
  redirect(routePath, { type = 'push', endThread = false, params, query } = {}) {
    const availableType = {
      push: 'push',
      replace: 'replace'
    };
    invariant(availableType[type], `重定向类型不正确, type = ${type}`);
    const { $router } = this.context.props;
    invariant($router, '请正确使用 withEnhanceRouter 对 page component 进行装饰');

    if (endThread) {
      // noinspection JSIgnoredPromiseFromCall
      setTimeout(() => {
        $router[type]({ url: routePath, params, query });
      });
      throw new EndThreadError();
    } else {
      $router[type]({ url: routePath, params, query });
    }
  }

  /**
   * 获取路由 params 参数, #/article/1
   * @returns {{}} - 返回路由参数如: {id: 1}
   */
  getRouteParams() {
    return this.context.props.$router.params;
  }

  /**
   * 获取路由 search params 参数, 如 #/article?id=1
   * @returns {{}} - 返回路由参数如: {id: 1}
   */
  getRouteQueryParams() {
    return this.context.props.$router.query;
  }

  /**
   * 强行跳转
   * @param {string} routePath - 重定向地址
   */
  forceRedirect(routePath) {
    invariant(
      !(DeviceUtils.isWeiXin() && DeviceUtils.isAndroid() && window.location.href === routePath),
      '在微信安卓端中, 跳转路由地址不能与当前地址一样, 否者将不会有任何效果'
    );
    window.location.href = routePath;
    throw new EndThreadError();
  }

  createInterceptions() {
    // 在 react 16.8 后开始出现大量 warning, 故逐步不建议使用
    // intercept(this.context, 'componentWillMount', this.$$componentWillMount, this);
    // intercept(this.context, 'componentWillReceiveProps', this.$$componentWillReceiveProps, this);
    // intercept(this.context, 'componentWillUpdate', this.$$componentWillUpdate, this);
    intercept(this.context, 'componentDidMount', () => protect()(::this.$$componentDidMount), this);
    intercept(this.context, 'shouldComponentUpdate', () => protect()(::this.$$shouldComponentUpdate), this);
    intercept(this.context, 'componentDidUpdate', () => protect()(::this.$$componentDidUpdate), this);
    intercept(this.context, 'componentWillUnmount', () => protect()(::this.$$componentWillUnmount), this);
  }
  /**
   * 组件加载完成, 等效于组件中的 componentDidMount
   * 当用于发起服务请求等 <br />
   * this.$ztServices.getUserInfo();
   */
  $$componentDidMount() {
    this.$$componentDidMountQueue.forEach(action => action());
  }

  $$shouldComponentUpdate() {}

  $$componentDidUpdate() {}

  /**
   * react 生命周期, 使用这个函数进行相关回收, 防止内存泄漏
   * @public
   */
  $$componentWillUnmount() {
    this.$$isUnmount = true;
    this.$$recycleQueue.forEach(recycle => recycle());
  }

  /**
   * 计算差值
   * @returns {number}
   */
  getDiffTimeBtwShowAndHide() {
    return this.$onShowTime - this.$onHideTime;
  }

  /**
   * 长时间离开当前页面
   */
  isAfkForLongTime() {
    return this.getDiffTimeBtwShowAndHide() > 1000 * 60 * 60 /* 1小时 */;
  }

  /**
   * 页面展示时, 如切换回当前页面 tab, 或手机后台回到页面时触发
   */
  $$onShow() {
    this.$onShowTime = +new Date();
    if (this.isAfkForLongTime()) {
      reload();
      throw new EndThreadError();
    }
  }

  /**
   * 页面隐藏时, 如切换 chrome tab 离开当前页面, 或手机切换到后台时触发
   */
  $$onHide() {
    this.$onHideTime = +new Date();
  }

  /**
   * 当手机从离线切换到在线时触发
   */
  $$onOnline() {}

  /**
   * 当手机从在线切换到离线时触发
   */
  $$onOffLine() {}
}



// WEBPACK FOOTER //
// ./src/mobx-store/vm/base-view-model.js